// src/app/features/brand/brandSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { brandService } from "./brandService";

const extractError = (error) =>
  error?.response?.data?.message ||
  error?.response?.data ||
  error?.message ||
  "Something went wrong";

/* =========================
   ✅ THUNKS (ONLY)
   (✅ keep variable names EXACTLY)
========================= */
export const getAllBrands = createAsyncThunk("brand/getAll", async (_, thunkAPI) => {
  try {
    return await brandService.getBrands();
  } catch (error) {
    return thunkAPI.rejectWithValue(extractError(error));
  }
});

export const getOneBrand = createAsyncThunk("brand/getOne", async (id, thunkAPI) => {
  try {
    return await brandService.getABrand(id);
  } catch (error) {
    return thunkAPI.rejectWithValue(extractError(error));
  }
});

/* =========================
   ✅ INITIAL STATE
========================= */
const initialState = {
  brands: [],
  brand: null, // ✅ single brand
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
};

/* =========================
   ✅ SLICE
========================= */
export const brandSlice = createSlice({
  name: "brand",
  initialState,
  reducers: {
    resetBrandState: (state) => {
      state.isError = false;
      state.isSuccess = false;
      state.isLoading = false;
      state.message = "";
    },
    clearBrand: (state) => {
      state.brand = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ===== GET ALL ===== */
      .addCase(getAllBrands.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.message = "";
      })
      .addCase(getAllBrands.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.message = "";
        state.brands = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getAllBrands.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload || "Failed to load brands";
        // keep existing brands; do not wipe silently
      })

      /* ===== GET ONE ===== */
      .addCase(getOneBrand.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.message = "";
      })
      .addCase(getOneBrand.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.message = "";
        state.brand = action.payload || null;
      })
      .addCase(getOneBrand.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload || "Failed to load brand";
        // keep existing brand; do not wipe silently
      });
  },
});

export default brandSlice.reducer;

/* =========================
   ✅ ACTIONS + SELECTORS
========================= */
export const { resetBrandState, clearBrand } = brandSlice.actions;

export const selectBrands = (state) => state?.brand?.brands || [];
export const selectBrand = (state) => state?.brand?.brand || null;
export const selectBrandLoading = (state) => Boolean(state?.brand?.isLoading);